import type { ReactNode } from "react";

type StatusTone = "action" | "monitor" | "healthy" | "neutral";

export type AdministrationStatusItem = Readonly<{
  label: "Needs action" | "Monitor" | "Healthy";
  tone: Exclude<StatusTone, "neutral">;
  value?: ReactNode;
  title: string;
  description: string;
}>;

export function AdministrationPageHeader({
  title,
  purpose,
  breadcrumb,
  status,
  meta,
}: Readonly<{
  title: string;
  purpose: string;
  breadcrumb?: string;
  status?: Readonly<{ label: string; tone: StatusTone }>;
  meta?: ReactNode;
}>): React.JSX.Element {
  return (
    <header className="page-header administration-page-header">
      <div>
        {breadcrumb === undefined ? null : <p className="page-breadcrumb">{breadcrumb}</p>}
        <h1>{title}</h1>
        <p className="page-purpose">{purpose}</p>
      </div>
      {status === undefined && meta === undefined ? null : (
        <div className="page-header-meta">
          {status === undefined ? null : (
            <span className="impact-status" data-tone={status.tone}>
              {status.label}
            </span>
          )}
          {meta}
        </div>
      )}
    </header>
  );
}

export function AdministrationSection({
  title,
  description,
  children,
  id,
  className,
}: Readonly<{
  title: string;
  description?: string;
  children: ReactNode;
  id: string;
  className?: string;
}>): React.JSX.Element {
  return (
    <section
      aria-labelledby={id}
      className={["administration-section", className].filter(Boolean).join(" ")}
    >
      <div className="section-heading">
        <div>
          <h2 id={id}>{title}</h2>
          {description === undefined ? null : <p>{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function AdministrationWorkSurface({
  id,
  title,
  purpose,
  state,
  consequence,
  action,
  audit,
  children,
  className,
}: Readonly<{
  id: string;
  title: string;
  purpose: string;
  state: Readonly<{ label: string; tone: StatusTone }>;
  consequence: string;
  action?: ReactNode;
  audit?: Readonly<{ summary: string; content: ReactNode }>;
  children: ReactNode;
  className?: string;
}>): React.JSX.Element {
  return (
    <section
      aria-labelledby={id}
      className={["administration-work-surface", className].filter(Boolean).join(" ")}
      data-tone={state.tone}
    >
      <header className="work-surface-header">
        <div>
          <h2 id={id}>{title}</h2>
          <p>{purpose}</p>
        </div>
        <strong className="work-surface-state">{state.label}</strong>
      </header>
      <p className="work-surface-consequence">{consequence}</p>
      <div className="work-surface-content">{children}</div>
      {action === undefined ? null : <div className="work-surface-action">{action}</div>}
      {audit === undefined ? null : (
        <AdministrationAuditDetails summary={audit.summary}>
          {audit.content}
        </AdministrationAuditDetails>
      )}
    </section>
  );
}

export function AdministrationStatusSummary({
  items,
}: Readonly<{ items: readonly AdministrationStatusItem[] }>): React.JSX.Element {
  return (
    <section className="administration-status-summary" aria-label="Current operational status">
      {items.map((item) => (
        <article className="administration-status-item" data-tone={item.tone} key={item.label}>
          <span className="status-label">{item.label}</span>
          <div>
            {item.value === undefined ? null : (
              <strong className="status-value">{item.value}</strong>
            )}
            <h2>{item.title}</h2>
            <p>{item.description}</p>
          </div>
        </article>
      ))}
    </section>
  );
}

export function AdministrationImpactPanel({
  label,
  title,
  description,
  action,
  evidence,
  tone = "neutral",
}: Readonly<{
  label: "Customer impact" | "Service impact" | "Privacy boundary";
  title: string;
  description: string;
  action?: ReactNode;
  evidence?: ReactNode;
  tone?: StatusTone;
}>): React.JSX.Element {
  return (
    <section className="administration-impact-panel" data-tone={tone}>
      <div className="administration-impact-copy">
        <p className="impact-label">{label}</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {action === undefined ? null : <div className="administration-impact-action">{action}</div>}
      {evidence === undefined ? null : (
        <details className="supporting-evidence">
          <summary>Supporting evidence</summary>
          <div>{evidence}</div>
        </details>
      )}
    </section>
  );
}

export function AdministrationAuditDetails({
  summary,
  children,
}: Readonly<{ summary: string; children: ReactNode }>): React.JSX.Element {
  return (
    <details className="audit-details">
      <summary>
        <span>
          <strong>Audit details</strong>
          <small>{summary}</small>
        </span>
        <span className="disclosure-label" aria-hidden="true">
          Show
        </span>
      </summary>
      <div className="audit-content">{children}</div>
    </details>
  );
}

export function AdministrationState({
  state,
  title,
  message,
  action,
}: Readonly<{
  state: "loading" | "empty" | "error";
  title: string;
  message: string;
  action?: ReactNode;
}>): React.JSX.Element {
  return (
    <section
      className="administration-state"
      aria-live={state === "loading" ? "polite" : undefined}
      aria-busy={state === "loading" ? true : undefined}
      role={state === "error" ? "alert" : undefined}
      data-state={state}
    >
      <h1>{title}</h1>
      <p>{message}</p>
      {action}
    </section>
  );
}
